/* eslint-disable react-hooks/exhaustive-deps */
import { Footer } from "components/footer/footer-component";
import { setLocalStorege, updateLocalStorege } from "data/cache/localstorage-cache";
import { getEnv } from "helper/window-helper";
import { UseAlert } from "hook/alert-hook";
import { SpinnerSC } from "pages/style/spinner/spinner-styled";
import { useEffect, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthUseCase } from "service/api/rest/auth-usecase";
import { RestUseCase } from "service/api/rest/rest-usecase";

const CONSULTOR_ONLINE_RETURN_URL = "consultor_online_return_url";
const ONLINE_CONSULTANT_ENTRY_ROUTE = "/comanalisaranalitico";
const ONLINE_CONSULTANT_ANALYTICAL_ROUTE = "/analiticoconsultoronline";

function getSanitizedCurrentUrl(): string {
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.delete("p");

  return `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
}

function updateVisibleUrl(returnUrl: string): void {
  const currentRoute = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  if (currentRoute !== returnUrl) {
    window.history.replaceState(null, "", returnUrl);
  }
}

function isSafeReturnUrl(returnUrl: string): boolean {
  return returnUrl === ONLINE_CONSULTANT_ENTRY_ROUTE
    || returnUrl.startsWith(`${ONLINE_CONSULTANT_ENTRY_ROUTE}?`)
    || returnUrl.startsWith(`${ONLINE_CONSULTANT_ENTRY_ROUTE}#`);
}

function saveReturnUrl(returnUrl: string): void {
  if (returnUrl.startsWith("/") && !returnUrl.startsWith("//") && isSafeReturnUrl(returnUrl)) {
    localStorage.setItem(CONSULTOR_ONLINE_RETURN_URL, returnUrl);
  }
}

function parseAutologinParams(value: string | null): { login: string, password: string } | null {
  if (!value) {
    return null;
  }

  const separatorIndex = value.indexOf(";");
  const login = separatorIndex >= 0 ? value.substring(0, separatorIndex).trim() : "";
  const password = separatorIndex >= 0 ? value.substring(separatorIndex + 1) : "";

  if (!login || !password) {
    return null;
  }

  return { login, password };
}

function isValidAnoIntCod(value: string | null): value is string {
  return value !== null && /^\d+$/.test(value) && Number(value) > 0;
}

function getResponseErrorMessage(responseBody: any): string | undefined {
  if (Array.isArray(responseBody)) {
    return responseBody.find((item) => item?.desc)?.desc;
  }

  return responseBody?.desc ?? responseBody?.message;
}

export const OnlineConsultantAnalyticalFactory: React.FC = () => {
  const ENDPOINT_LOGIN = getEnv("REACT_APP_END_POINT_LOGIN")!;
  const LOCALSTORAGE_TOKEN_USER_LOGIN = getEnv("REACT_APP_LOCALSTORAGE_TOKEN_USER_LOGIN")!;
  const ENDPOINT_ONLINE_CONSULTANT = getEnv("REACT_APP_END_POINT_ONLINE_CONSULTANT")!;
  const LOCALSTORAGE_PLANNING_ANALITYCAL = getEnv("REACT_APP_LOCALSTORAGE_PLANNING_ANALYTICAL")!;

  const { showAlert } = UseAlert();
  const [queryParameters] = useSearchParams();
  const navigate = useNavigate();
  const [errorMessage, updateErrorMessage] = useState<string>();
  const started = useRef<boolean>(false);

  const anoIntCod = queryParameters.get("anoIntCod");
  const p = queryParameters.get("p");

  function showControlledError(content: string): void {
    updateErrorMessage(content);
    showAlert({ show: true, content, color: "danger", time: 5000 });
  }

  async function authentication(params: { login: string, password: string }): Promise<boolean> {
    try {
      const response = await new AuthUseCase(`${ENDPOINT_LOGIN}/login-consultor-online`)
        .Post({
          data: {
            username: params.login,
            password: params.password,
          },
        });

      if (response.statusCode === 200) {
        setLocalStorege(LOCALSTORAGE_TOKEN_USER_LOGIN, response.body);
        return true;
      }
    } catch (error) {
    }

    return false;
  }

  async function findPlanning(validAnoIntCod: string): Promise<{ success: boolean, message?: string }> {
    const response = await new RestUseCase(`${ENDPOINT_ONLINE_CONSULTANT}/filtros/${validAnoIntCod}`).Get();
    const filtros = response.body?.filtros;

    if (response.statusCode === 200 && filtros !== undefined && filtros !== null) {
      updateLocalStorege(LOCALSTORAGE_PLANNING_ANALITYCAL, filtros);
      return { success: true };
    }

    return {
      success: false,
      message: getResponseErrorMessage(response.body),
    };
  }

  async function loadPlanningAndRedirect(validAnoIntCod: string, shouldReload: boolean): Promise<void> {
    const planning = await findPlanning(validAnoIntCod);

    if (!planning.success) {
      showControlledError(planning.message ?? "Nao foi possivel carregar os filtros do planejamento.");
      return;
    }

    localStorage.removeItem(CONSULTOR_ONLINE_RETURN_URL);

    if (shouldReload) {
      window.location.replace(ONLINE_CONSULTANT_ANALYTICAL_ROUTE);
      return;
    }

    navigate(ONLINE_CONSULTANT_ANALYTICAL_ROUTE, { replace: true });
  }

  useEffect(() => {
    if (started.current) {
      return;
    }

    started.current = true;

    const run = async () => {
      const returnUrl = getSanitizedCurrentUrl();
      updateVisibleUrl(returnUrl);

      if (!isValidAnoIntCod(anoIntCod)) {
        showControlledError("Link do Consultor Online invalido. O parametro anoIntCod e obrigatorio.");
        return;
      }

      const hasToken = Boolean(localStorage.getItem(LOCALSTORAGE_TOKEN_USER_LOGIN));

      if (hasToken) {
        await loadPlanningAndRedirect(anoIntCod, false);
        return;
      }

      const autologinParams = parseAutologinParams(p);

      if (autologinParams) {
        const authenticated = await authentication(autologinParams);

        if (authenticated) {
          await loadPlanningAndRedirect(anoIntCod, true);
          return;
        }
      }

      saveReturnUrl(returnUrl);
      window.location.replace("/");
    };

    run().catch(() => showControlledError("Ops!!! Algo deu errado tente novamente mais tarde..."));
  }, []);

  return (
    <>
      <Container style={{ height: "90vh", textAlign: "center", alignContent: "center" }}>
        {errorMessage ? (
          <h6 className="mt-3">{errorMessage}</h6>
        ) : (
          <>
            <h6 className="mt-3">Redirecionando para Analitico, caso nao seja redirecionado verifique se o link da pagina esta correto.</h6>
            <SpinnerSC style={{ height: "10vh" }}>
              <div className="spinner-border text-secondary" role="status"></div>
            </SpinnerSC>
          </>
        )}
      </Container>
      <Footer />
    </>
  );
};
